import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, MessageSquare, Camera, MoreHorizontal, Utensils, Settings, LogOut, Bug, Wallet } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { useTheme } from '@/lib/ThemeContext';
import { useEnabledModulesForUser } from '@/hooks/useBranchModules';

const NAV_ITEMS_BASE = [
  { name: 'Start', icon: Home, page: '/Home', tutorialId: 'nav-home' },
  { name: 'Wiadomości', icon: MessageSquare, page: '/Messages', tutorialId: 'nav-messages' },
  { name: 'Zdjęcia', icon: Camera, page: '/Photos', tutorialId: 'nav-photos', module: 'media' },
];

const JADLOSPIS_ITEM = { name: 'Jadłospis', icon: Utensils, page: '/WeeklyMenu', tutorialId: 'nav-menu', module: 'jadlospis' };

const MORE_ITEMS_BASE = [
  JADLOSPIS_ITEM,
  { name: 'Rozliczenia', icon: Wallet, page: '/ParentBilling', tutorialId: 'nav-billing', module: 'rozliczenia' },
  { name: 'Ustawienia', icon: Settings, page: '/Settings', tutorialId: 'nav-settings' },
];

export default function BottomNav({ onLogout, onReportBug }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const { getActiveColors } = useTheme();
  const themeColors = getActiveColors();
  const { isModuleEnabled } = useEnabledModulesForUser();

  const navItems = useMemo(() => {
    const base = NAV_ITEMS_BASE.filter((item) => !item.module || isModuleEnabled(item.module));
    if (isModuleEnabled('jadlospis')) base.push(JADLOSPIS_ITEM);
    return base;
  }, [isModuleEnabled]);
  const moreItems = useMemo(
    () =>
      MORE_ITEMS_BASE.filter((item) => !item.module || isModuleEnabled(item.module))
        .filter((item) => !(item.page === '/WeeklyMenu' && isModuleEnabled('jadlospis'))),
    [isModuleEnabled]
  );

  const isActive = (page) => {
    return location.pathname === page || location.pathname === page.toLowerCase();
  };

  const handleNavClick = (page) => {
    navigate(page);
  };

  const handleMoreItemClick = (page) => {
    setMoreOpen(false);
    navigate(page);
  };

  return (
    <>
      {/* Bottom Navigation Bar - Uses theme colors */}
      <nav className={cn("fixed bottom-0 left-0 right-0 z-50 safe-area-bottom", themeColors.headerBg)}>
        <div className="flex items-center justify-around h-14 min-h-[56px] px-0.5 sm:px-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.page);
            return (
              <button
                key={item.page}
                onClick={() => handleNavClick(item.page)}
                data-tutorial={item.tutorialId}
                className={cn(
                  "flex flex-col items-center justify-center flex-1 min-w-0 h-full gap-0.5 py-1.5 transition-colors",
                  active ? "text-white" : "text-white/70 hover:text-white"
                )}
              >
                <Icon className="w-5 h-5 shrink-0" />
                <span className={cn(
                  "text-[11px] leading-tight text-center truncate max-w-full px-0.5",
                  active ? "font-semibold" : "font-medium"
                )}>
                  {item.name}
                </span>
              </button>
            );
          })}

          {/* More Button */}
          <button
            onClick={() => setMoreOpen(true)}
            className={cn(
              "flex flex-col items-center justify-center flex-1 min-w-0 h-full gap-0.5 py-1.5 transition-colors",
              moreOpen ? "text-white" : "text-white/70 hover:text-white"
            )}
          >
            <MoreHorizontal className="w-5 h-5 shrink-0" />
            <span className="text-[11px] leading-tight font-medium truncate max-w-full px-0.5">Inne</span>
          </button>
        </div>
      </nav>

      {/* More Sheet (Bottom Drawer) */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl">
          <SheetHeader className="pb-4">
            <SheetTitle>Inne opcje</SheetTitle>
          </SheetHeader>

          <div className="space-y-2 pb-8">
            {moreItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.page);
              return (
                <button
                  key={item.page}
                  onClick={() => handleMoreItemClick(item.page)}
                  className={cn(
                    "flex items-center gap-4 w-full p-4 rounded-xl transition-colors",
                    active
                      ? "bg-blue-50 text-blue-600"
                      : "hover:bg-slate-50 text-slate-700"
                  )}
                >
                  <div className={cn(
                    "p-2 rounded-lg",
                    active ? "bg-blue-100" : "bg-slate-100"
                  )}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className={cn(
                    "font-medium",
                    active && "font-semibold"
                  )}>
                    {item.name}
                  </span>
                </button>
              );
            })}

            {/* Divider */}
            <div className="border-t border-slate-200 my-4" />

            {/* Report Bug Button */}
            <button
              onClick={() => {
                setMoreOpen(false);
                onReportBug?.();
              }}
              className="flex items-center gap-4 w-full p-4 rounded-xl transition-colors hover:bg-amber-50 text-amber-700"
            >
              <div className="p-2 rounded-lg bg-amber-100">
                <Bug className="w-5 h-5" />
              </div>
              <span className="font-medium">Zgłoś problem</span>
            </button>

            {/* Logout Button */}
            <button
              onClick={() => {
                setMoreOpen(false);
                onLogout?.();
              }}
              className="flex items-center gap-4 w-full p-4 rounded-xl transition-colors hover:bg-red-50 text-red-600"
            >
              <div className="p-2 rounded-lg bg-red-100">
                <LogOut className="w-5 h-5" />
              </div>
              <span className="font-medium">Wyloguj</span>
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
